(() => {
  const BUTTON_SELECTOR = '[data-multilikes-api-product-button]';
  const favoriteVariantIds = new Set();
  let loadPromise = null;
  let loadedCustomerId = '';

  const numericId = (value) => String(value || '').split('/').pop();

  const buttons = () => Array.from(document.querySelectorAll(BUTTON_SELECTOR));

  const updateButton = (button) => {
    const isActive = favoriteVariantIds.has(String(button.dataset.variantId || ''));
    const title = button.dataset.productTitle || '商品';
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', `${title}をお気に入り${isActive ? 'から削除' : 'に追加'}`);
  };

  const updateButtons = (variantId) => {
    buttons().forEach((button) => {
      if (!variantId || button.dataset.variantId === variantId) updateButton(button);
    });
  };

  const requestUrl = (button) => {
    const url = new URL(button.dataset.multilikesEndpoint || '/apps/multilikes-endpoint', window.location.origin);
    url.searchParams.set('customer_id', button.dataset.customerId);
    url.searchParams.set('mode', 'customer_wishlist');
    url.searchParams.set('target_type', 'product');
    url.searchParams.set('limit', '250');
    return url;
  };

  const loadFavorites = async (button, { force = false } = {}) => {
    const customerId = button.dataset.customerId || '';
    if (!customerId) return;
    if (!force && loadPromise && loadedCustomerId === customerId) return loadPromise;

    loadedCustomerId = customerId;
    loadPromise = (async () => {
      const response = await fetch(requestUrl(button), { headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Wishlist API request failed');

      favoriteVariantIds.clear();
      (Array.isArray(result.data) ? result.data : []).forEach((item) => {
        const variantId = numericId(item.id || item.variantId);
        if (variantId) favoriteVariantIds.add(variantId);
      });
      updateButtons();
    })().catch((error) => {
      loadPromise = null;
      console.error('MultiLikes product status request failed:', error);
      throw error;
    });

    return loadPromise;
  };

  const toggleFavorite = async (button) => {
    const customerId = button.dataset.customerId || '';
    if (!customerId) {
      window.location.assign(button.dataset.loginUrl || '/account/login');
      return;
    }

    // 送信直前に最新状態を取り直す。カート更新などでボタンの見た目だけが未登録に
    // 戻っている場合に、登録済みと判定して remove を送ってしまうのを防ぐ
    try {
      await loadFavorites(button, { force: true });
    } catch (_) {
      return;
    }
    const variantId = String(button.dataset.variantId || '');
    const isActive = favoriteVariantIds.has(variantId);
    const body = new URLSearchParams({
      customer_id: customerId,
      action: isActive ? 'remove' : 'add',
      target_type: 'product',
      product_id: button.dataset.productId || '',
      variant_id: variantId
    });

    button.classList.add('is-loading');
    button.disabled = true;
    try {
      const response = await fetch(button.dataset.multilikesEndpoint || '/apps/multilikes-endpoint', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Wishlist update failed');

      if (isActive) favoriteVariantIds.delete(variantId);
      else favoriteVariantIds.add(variantId);
      updateButtons(variantId);
    } catch (error) {
      console.error('MultiLikes product update failed:', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    toggleFavorite(button);
  });

  const initialize = () => {
    const button = document.querySelector(BUTTON_SELECTOR);
    if (!button) return;
    updateButtons();
    loadFavorites(button).catch(() => {});
  };

  // セクション再描画などでボタンが差し替わると、サーバー側は常に未登録の状態で
  // 出力するため見た目が戻ってしまう。追加されたボタンを検知して塗り直す
  let updateScheduled = false;
  const scheduleUpdate = () => {
    if (updateScheduled) return;
    updateScheduled = true;
    requestAnimationFrame(() => {
      updateScheduled = false;
      updateButtons();
    });
  };

  const observeButtons = () => {
    if (!document.body) return;
    new MutationObserver((mutations) => {
      const hasNewButton = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(BUTTON_SELECTOR) || node.querySelector?.(BUTTON_SELECTOR))
        )
      );
      if (hasNewButton) scheduleUpdate();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      observeButtons();
    }, { once: true });
  } else {
    initialize();
    observeButtons();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('cart:updated', scheduleUpdate);
})();
